import { useEffect, useState, useMemo } from "react";
import { format } from "date-fns";
import { toast } from "sonner";
import {
  Loader2,
  Search,
  Plus,
  Mail,
  Phone,
  MapPin,
  CalendarDays,
  FileText,
  Trash2,
  ArrowLeft,
  Save,
  UserPlus,
  X,
  Send,
  ClipboardList,
  Check,
  RefreshCcw,
} from "lucide-react";
import { api, formatApiErrorDetail, API } from "@/lib/api";
import SessionNotesEditor from "@/components/admin/SessionNotesEditor";

export default function ClientsManager() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/clients", { params: showArchived ? { include_archived: 1 } : {} });
      setClients(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [showArchived]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return clients;
    return clients.filter(
      (c) =>
        c.name?.toLowerCase().includes(term) ||
        c.email?.toLowerCase().includes(term) ||
        c.phone?.toLowerCase().includes(term)
    );
  }, [clients, q]);

  if (selectedId) {
    return (
      <ClientDetail
        clientId={selectedId}
        onBack={() => { setSelectedId(null); load(); }}
      />
    );
  }

  return (
    <div data-testid="clients-manager">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-ink-soft mb-1">Clients</div>
          <h2 className="font-serif text-3xl text-ink">Everyone you've worked with.</h2>
          <p className="text-sm text-ink-soft mt-1">{clients.length} record{clients.length === 1 ? "" : "s"} — auto-created from bookings & enquiries.</p>
        </div>
        <div className="flex gap-3 items-center">
          <label className="inline-flex items-center gap-2 text-xs text-ink-soft cursor-pointer select-none" data-testid="clients-show-archived-wrap">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="h-3.5 w-3.5 accent-navy"
              data-testid="clients-show-archived"
            />
            Show archived
          </label>
          <div className="relative">
            <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-soft" />
            <input
              data-testid="clients-search"
              placeholder="Search name, email, phone…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-10 pr-4 py-2.5 bg-cream border border-sand/80 rounded-full text-sm focus:outline-none focus:border-navy w-72"
            />
          </div>
          <button
            onClick={() => setShowCreate(true)}
            data-testid="clients-new-button"
            className="inline-flex items-center gap-2 rounded-full bg-navy text-cream px-5 py-2.5 text-sm font-medium hover:bg-navy-deep transition-colors"
          >
            <UserPlus size={14} /> New client
          </button>
        </div>
      </div>

      {loading ? (
        <div className="py-16 flex items-center justify-center text-ink-muted">
          <Loader2 size={20} className="animate-spin mr-2" /> Loading…
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-ink-soft italic font-serif text-lg">
          {clients.length === 0
            ? "No clients yet. They'll appear here once you have bookings or enquiries."
            : "No clients match that search."}
        </div>
      ) : (
        <div className="grid gap-3" data-testid="clients-list">
          {filtered.map((c) => (
            <button
              key={c.id}
              data-testid={`client-row-${c.id}`}
              onClick={() => setSelectedId(c.id)}
              className="text-left bg-cream-soft border border-sand/70 rounded-2xl p-5 hover:border-navy/60 hover:-translate-y-0.5 transition-all"
            >
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="font-serif text-xl text-ink flex items-center gap-2">
                    {c.name || "(no name)"}
                    {c.archived && (
                      <span className="text-[10px] uppercase tracking-[0.18em] text-ink-soft bg-cream border border-sand px-2 py-0.5 rounded-full" data-testid="client-archived-badge">Archived</span>
                    )}
                  </div>
                  <div className="text-sm text-ink-muted flex items-center gap-4 mt-1 flex-wrap">
                    <span className="inline-flex items-center gap-1.5"><Mail size={12} />{c.email}</span>
                    {c.phone && <span className="inline-flex items-center gap-1.5"><Phone size={12} />{c.phone}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-6 text-right">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-ink-soft">Sessions</div>
                    <div className="font-serif text-xl text-navy">{c.session_count || 0}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-ink-soft">Last seen</div>
                    <div className="text-sm text-ink">
                      {c.last_session_date ? format(new Date(c.last_session_date), "d MMM yy") : "—"}
                    </div>
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {showCreate && (
        <NewClientModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />
      )}
    </div>
  );
}

function ClientDetail({ clientId, onBack }) {
  const [data, setData] = useState(null);
  const [emails, setEmails] = useState([]);
  const [intakeForms, setIntakeForms] = useState([]);
  const [notesHistory, setNotesHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [notesBooking, setNotesBooking] = useState(null);  // a booking opened for inline notes editing
  const [draft, setDraft] = useState({ name: "", phone: "", address: "", notes: "" });

  const load = async () => {
    setLoading(true);
    try {
      const [r, em, ifs, sn] = await Promise.all([
        api.get(`/admin/clients/${clientId}`),
        api.get(`/admin/clients/${clientId}/emails`).catch(() => ({ data: [] })),
        api.get(`/admin/clients/${clientId}/intake-forms`).catch(() => ({ data: [] })),
        api.get(`/admin/clients/${clientId}/session-notes`).catch(() => ({ data: [] })),
      ]);
      setData(r.data);
      setEmails(em.data || []);
      setIntakeForms(ifs.data || []);
      setNotesHistory(sn.data || []);
      setDraft({
        name: r.data.client.name || "",
        phone: r.data.client.phone || "",
        address: r.data.client.address || "",
        notes: r.data.client.notes || "",
      });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [clientId]);

  const resendIntake = async (formId) => {
    if (!window.confirm("Resend the intake form email to this client?")) return;
    try {
      await api.post(`/admin/intake-forms/${formId}/resend`);
      toast.success("Intake invitation resent");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/clients/${clientId}`, draft);
      toast.success("Client updated");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    if (!window.confirm("Archive this client? Their profile will be hidden from lists (you can restore it any time). Bookings, invoices and session notes stay on file for record-keeping.")) return;
    try {
      await api.delete(`/admin/clients/${clientId}`);
      toast.success("Client archived");
      onBack();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const restore = async () => {
    try {
      await api.post(`/admin/clients/${clientId}/restore`);
      toast.success("Client restored");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (loading) {
    return <div className="py-16 flex items-center justify-center text-ink-muted"><Loader2 size={20} className="animate-spin mr-2" /> Loading client…</div>;
  }
  if (!data) return null;

  const { client, bookings, invoices } = data;

  return (
    <div data-testid="client-detail">
      <button onClick={onBack} className="inline-flex items-center gap-2 text-sm text-ink-soft hover:text-navy mb-6">
        <ArrowLeft size={14} /> Back to clients
      </button>

      {notesBooking && (
        <ClientNotesDrawer
          booking={notesBooking}
          onClose={() => setNotesBooking(null)}
          onCompleted={() => { setNotesBooking(null); load(); }}
        />
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-cream-soft border border-sand/70 rounded-2xl p-6">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="text-xs uppercase tracking-[0.22em] text-ink-soft mb-2">Profile</div>
                <h2 className="font-serif text-3xl text-ink mb-1">{client.name || "(no name)"}</h2>
                <a href={`mailto:${client.email}`} className="text-navy hover:underline text-sm">{client.email}</a>
              </div>
              <button
                onClick={() => setShowEmail(true)}
                data-testid="client-email-button"
                className="inline-flex items-center gap-2 rounded-full bg-navy-light text-cream px-5 py-2.5 text-sm font-medium hover:bg-navy transition-colors"
              >
                <Send size={14} /> Send email
              </button>
            </div>
            <div className="grid sm:grid-cols-2 gap-4 mt-6">
              <Field label="Full name" value={draft.name} onChange={(v) => setDraft({ ...draft, name: v })} testid="client-name" />
              <Field label="Phone" value={draft.phone} onChange={(v) => setDraft({ ...draft, phone: v })} testid="client-phone" />
            </div>
            <Field label="Address" value={draft.address} onChange={(v) => setDraft({ ...draft, address: v })} testid="client-address" textarea rows={3} />
            <Field
              label="Private session notes (only you see these)"
              value={draft.notes}
              onChange={(v) => setDraft({ ...draft, notes: v })}
              testid="client-notes"
              textarea
              rows={10}
            />
            <div className="flex justify-between items-center mt-5">
              {client.archived ? (
                <button onClick={restore} data-testid="client-restore-button" className="inline-flex items-center gap-1.5 text-xs text-navy hover:text-navy-deep">
                  <RefreshCcw size={12} /> Restore client
                </button>
              ) : (
                <button onClick={del} data-testid="client-archive-button" className="inline-flex items-center gap-1.5 text-xs text-red-700 hover:text-red-900">
                  <Trash2 size={12} /> Archive client
                </button>
              )}
              <button
                onClick={save}
                disabled={saving}
                data-testid="client-save-button"
                className="inline-flex items-center gap-2 rounded-full bg-navy text-cream px-6 py-2.5 text-sm font-medium hover:bg-navy-deep transition-colors disabled:opacity-60"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save changes
              </button>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-cream-soft border border-sand/70 rounded-2xl p-6">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-ink-soft mb-4">
              <CalendarDays size={12} /> Sessions ({bookings.length})
            </div>
            {bookings.length === 0 ? (
              <div className="text-sm text-ink-soft italic">No sessions yet.</div>
            ) : (
              <ul className="space-y-2">
                {bookings.slice(0, 8).map((b) => {
                  const hasNotes = notesHistory.some((n) => n.booking_id === b.id);
                  return (
                    <li key={b.id} className="flex items-center justify-between gap-3 border-b border-sand/40 last:border-0 pb-2 last:pb-0">
                      <div className="min-w-0">
                        <div className="text-sm text-ink">{format(new Date(b.date), "d MMM")} · {b.start_time}</div>
                        <div className="text-xs text-ink-soft truncate">{b.service_name}</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setNotesBooking(b)}
                        data-testid={`session-add-notes-${b.id}`}
                        className="inline-flex items-center gap-1.5 rounded-full bg-cream border border-navy/30 text-navy hover:bg-navy hover:text-cream px-3 py-1 text-[11px] font-medium transition-colors whitespace-nowrap"
                      >
                        <FileText size={11} /> {hasNotes ? "Open notes" : "Add notes"}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <div className="bg-cream-soft border border-sand/70 rounded-2xl p-6">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-ink-soft mb-4">
              <FileText size={12} /> Invoices ({invoices.length})
            </div>
            {invoices.length === 0 ? (
              <div className="text-sm text-ink-soft italic">No invoices yet.</div>
            ) : (
              <ul className="space-y-2">
                {invoices.slice(0, 8).map((i) => (
                  <li key={i.id} className="flex items-center justify-between text-sm border-b border-sand/40 last:border-0 pb-2 last:pb-0">
                    <span className="text-ink font-mono">{i.number}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${i.status === "paid" ? "bg-navy/10 text-navy" : "bg-cream border border-sand text-ink-soft"}`}>${i.total.toFixed(2)} · {i.status}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="bg-cream-soft border border-sand/70 rounded-2xl p-6">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-ink-soft mb-4">
              <Send size={12} /> Emails sent ({emails.length})
            </div>
            {emails.length === 0 ? (
              <div className="text-sm text-ink-soft italic">No emails sent from here yet.</div>
            ) : (
              <ul className="space-y-3">
                {emails.slice(0, 5).map((m) => (
                  <li key={m.id} className="border-b border-sand/40 last:border-0 pb-3 last:pb-0">
                    <div className="text-xs text-ink-soft">{format(new Date(m.sent_at), "d MMM · h:mm a")}</div>
                    <div className="text-sm text-ink font-medium truncate">{m.subject}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <IntakeFormsCard forms={intakeForms} onResend={resendIntake} />
          <SessionNotesHistoryCard notesHistory={notesHistory} />
        </div>
      </div>

      {showEmail && (
        <EmailClientModal
          client={client}
          onClose={() => setShowEmail(false)}
          onSent={() => { setShowEmail(false); load(); }}
        />
      )}
    </div>
  );
}

function Field({ label, value, onChange, testid, textarea, rows = 2 }) {
  const inputCls = "w-full bg-cream border border-sand/80 rounded-2xl px-4 py-3 text-ink focus:outline-none focus:border-navy focus:ring-1 focus:ring-navy text-sm";
  return (
    <div className="mt-4">
      <label className="text-[11px] uppercase tracking-[0.18em] text-ink-soft mb-2 block">{label}</label>
      {textarea ? (
        <textarea data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} rows={rows} className={`${inputCls} resize-y font-sans`} />
      ) : (
        <input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className={inputCls} />
      )}
    </div>
  );
}

function NewClientModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", notes: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/clients", form);
      toast.success("Client added");
      onCreated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-ink/40 backdrop-blur-sm z-50 flex items-start justify-center p-6 overflow-y-auto" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        data-testid="new-client-modal"
        className="bg-cream-soft border border-sand/70 rounded-3xl p-7 w-full max-w-lg mt-12 shadow-2xl"
      >
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-ink-soft">New client</div>
            <h3 className="font-serif text-2xl text-ink">Add someone manually.</h3>
          </div>
          <button type="button" onClick={onClose} className="text-ink-soft hover:text-ink"><X size={20} /></button>
        </div>
        <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="new-client-name" />
        <Field label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="new-client-email" />
        <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="new-client-phone" />
        <Field label="Address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} testid="new-client-address" textarea rows={2} />
        <Field label="Initial notes" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} testid="new-client-notes" textarea rows={3} />
        <button
          type="submit"
          disabled={saving || !form.name || !form.email}
          data-testid="new-client-submit"
          className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-navy text-cream px-6 py-3 text-sm font-medium hover:bg-navy-deep transition-colors disabled:opacity-50"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add client
        </button>
      </form>
    </div>
  );
}


function EmailClientModal({ client, onClose, onSent }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  const send = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const r = await api.post(`/admin/clients/${client.id}/email`, { subject, body });
      toast.success(r.data.sent ? "Email sent" : "Email logged (no Resend key)");
      onSent();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const inputCls = "w-full bg-cream border border-sand/80 rounded-2xl px-4 py-3 text-ink focus:outline-none focus:border-navy focus:ring-1 focus:ring-navy text-sm";
  const labelCls = "text-[11px] uppercase tracking-[0.18em] text-ink-soft mb-1.5 block";

  return (
    <div className="fixed inset-0 bg-ink/40 backdrop-blur-sm z-50 flex items-start justify-center p-6 overflow-y-auto" onClick={onClose}>
      <form
        onSubmit={send}
        onClick={(e) => e.stopPropagation()}
        data-testid="email-client-modal"
        className="bg-cream-soft border border-sand/70 rounded-3xl p-7 w-full max-w-xl mt-12 mb-12 shadow-2xl"
      >
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-ink-soft">Send email</div>
            <h3 className="font-serif text-2xl text-ink">A note to {client.name?.split(" ")[0] || "your client"}</h3>
            <div className="text-xs text-ink-soft mt-1">to <span className="text-ink">{client.email}</span></div>
          </div>
          <button type="button" onClick={onClose} className="text-ink-soft hover:text-ink"><X size={20} /></button>
        </div>

        <div>
          <label className={labelCls}>Subject</label>
          <input
            className={inputCls}
            required
            placeholder="Hi {first name} — quick note about…"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            data-testid="email-subject"
          />
        </div>

        <div className="mt-4">
          <label className={labelCls}>Message</label>
          <textarea
            className={`${inputCls} resize-y font-sans leading-relaxed`}
            rows={10}
            required
            placeholder={"Write naturally — paragraph breaks are preserved. The email will sign off automatically as Jesse from A Mindful Space Counselling."}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            data-testid="email-body"
          />
          <div className="text-[11px] text-ink-soft mt-1.5">
            Sent from <span className="text-ink">{client.email && "info@amindfulspacecounselling.com"}</span> · Replies go straight to your inbox.
          </div>
        </div>

        <button
          type="submit"
          disabled={sending || !subject.trim() || !body.trim()}
          data-testid="email-send-button"
          className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-navy text-cream px-6 py-3 text-sm font-medium hover:bg-navy-deep transition-colors disabled:opacity-50"
        >
          {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send email
        </button>
      </form>
    </div>
  );
}


function IntakeFormsCard({ forms, onResend }) {
  const [openId, setOpenId] = useState(null);
  if (!forms || forms.length === 0) {
    return (
      <div className="bg-cream-soft border border-sand/70 rounded-2xl p-6">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-ink-soft mb-2">
          <ClipboardList size={12} /> Intake forms
        </div>
        <div className="text-sm text-ink-soft italic">Sent automatically after this client's first booking. Nothing here yet.</div>
      </div>
    );
  }
  return (
    <div className="bg-cream-soft border border-sand/70 rounded-2xl p-6" data-testid="intake-forms-card">
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-ink-soft mb-4">
        <ClipboardList size={12} /> Intake forms ({forms.length})
      </div>
      <ul className="space-y-3">
        {forms.map((f) => (
          <li key={f.id} className="border-b border-sand/40 last:border-0 pb-3 last:pb-0">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                {f.status === "submitted" ? (
                  <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-navy bg-navy/10 px-2.5 py-1 rounded-full"><Check size={11} /> Submitted</span>
                ) : (
                  <span className="text-[11px] uppercase tracking-[0.18em] text-ink-soft bg-cream border border-sand px-2.5 py-1 rounded-full">Pending</span>
                )}
                <span className="text-xs text-ink-soft">{format(new Date(f.created_at), "d MMM yyyy")}</span>
              </div>
              <div className="flex gap-2">
                {f.status === "pending" && (
                  <button
                    type="button"
                    onClick={() => onResend(f.id)}
                    className="inline-flex items-center gap-1.5 text-xs text-navy hover:underline"
                  >
                    <RefreshCcw size={12} /> Resend email
                  </button>
                )}
                {f.status === "submitted" && (
                  <button
                    type="button"
                    onClick={() => setOpenId(openId === f.id ? null : f.id)}
                    data-testid={`intake-toggle-${f.id}`}
                    className="text-xs text-navy hover:underline"
                  >
                    {openId === f.id ? "Hide" : "View answers"}
                  </button>
                )}
              </div>
            </div>
            {openId === f.id && f.status === "submitted" && <IntakeAnswers form={f} />}
          </li>
        ))}
      </ul>
    </div>
  );
}

function IntakeAnswers({ form }) {
  const i = form.intake || {};
  const a = form.agreement || {};
  const Row = ({ label, value }) => (
    <div className="py-2 border-b border-sand/30 last:border-0">
      <div className="text-[10px] uppercase tracking-[0.18em] text-ink-soft mb-0.5">{label}</div>
      <div className="text-sm text-ink whitespace-pre-wrap">{value || <span className="text-ink-soft italic">—</span>}</div>
    </div>
  );
  const ec = i.emergency_contact || {};
  return (
    <div className="mt-4 bg-cream rounded-xl border border-sand/60 p-4 text-sm">
      <div className="grid sm:grid-cols-2 gap-x-6">
        <Row label="Full legal name" value={i.full_legal_name} />
        <Row label="Preferred name" value={i.preferred_name} />
        <Row label="Date of birth" value={i.date_of_birth} />
        <Row label="Phone" value={i.phone} />
        <Row label="Email" value={i.email} />
        <Row label="Occupation" value={i.occupation} />
        <Row label="Address" value={i.address} />
        <Row label="Emergency contact" value={`${ec.name || ""}${ec.relationship ? " (" + ec.relationship + ")" : ""}${ec.phone ? " · " + ec.phone : ""}`} />
      </div>
      <Row label="Primary reasons for counselling" value={i.primary_reasons} />
      <Row label="Duration of difficulties" value={i.duration} />
      <Row label="Goals" value={i.goals} />
      <Row label="Current distress level (1–10)" value={String(i.distress_level || "")} />
      <Row label="Mental health history" value={i.mental_health_history} />
      <Row label="Prior therapy" value={i.prior_therapy} />
      <Row label="Medications" value={i.medications} />
      <Row label="Other healthcare professionals" value={i.other_treatment} />
      <Row label="Physical health" value={i.physical_health} />
      <Row label="Alcohol / recreational drugs" value={i.substance_use} />
      <div className="mt-4 pt-3 border-t border-sand/40">
        <div className="text-[10px] uppercase tracking-[0.18em] text-ink-soft mb-1">Counselling agreement</div>
        <div className="text-sm text-ink">
          Signed by <strong className="font-serif italic">{a.typed_signature}</strong> on {a.signed_on} ·
          <span className="ml-1 text-ink-soft">All three clauses agreed</span>
        </div>
      </div>
    </div>
  );
}

function SessionNotesHistoryCard({ notesHistory }) {
  const [openId, setOpenId] = useState(null);
  const token = typeof window !== "undefined" ? localStorage.getItem("ams_token") : "";
  if (!notesHistory || notesHistory.length === 0) {
    return (
      <div className="bg-cream-soft border border-sand/70 rounded-2xl p-6">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-ink-soft mb-2">
          <FileText size={12} /> Session notes
        </div>
        <div className="text-sm text-ink-soft italic">Notes appear here once you start writing them on a completed session.</div>
      </div>
    );
  }
  return (
    <div className="bg-cream-soft border border-sand/70 rounded-2xl p-6" data-testid="client-notes-history">
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-ink-soft mb-4">
        <FileText size={12} /> Session notes timeline ({notesHistory.length})
      </div>
      <ul className="space-y-3">
        {notesHistory.map((n) => {
          const isOpen = openId === n.id;
          const pdfUrl = `${API}/admin/bookings/${n.booking_id}/notes/pdf?auth=${encodeURIComponent(token || "")}`;
          const attUrl = n.attachment
            ? `${API}/admin/bookings/${n.booking_id}/notes/attachment?auth=${encodeURIComponent(token || "")}`
            : null;
          return (
            <li key={n.id} className="border-b border-sand/40 last:border-0 pb-3 last:pb-0">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-serif text-base text-ink">Session #{n.session_number}</span>
                  <span className="text-xs text-ink-soft">{n.session_date}</span>
                  {n.status === "complete" ? (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.16em] text-navy bg-navy/10 px-2 py-0.5 rounded-full">Complete</span>
                  ) : (
                    <span className="text-[10px] uppercase tracking-[0.16em] text-ink-soft bg-cream border border-sand px-2 py-0.5 rounded-full">Draft</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {attUrl && (
                    <a href={attUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-navy hover:underline">
                      <Mail size={10} /> attachment
                    </a>
                  )}
                  <a href={pdfUrl} target="_blank" rel="noopener noreferrer" data-testid={`notes-pdf-${n.booking_id}`} className="inline-flex items-center gap-1 text-xs text-navy hover:underline">
                    PDF
                  </a>
                  <button
                    type="button"
                    onClick={() => setOpenId(isOpen ? null : n.id)}
                    data-testid={`notes-toggle-${n.id}`}
                    className="text-xs text-navy hover:underline"
                  >
                    {isOpen ? "Hide" : "Read"}
                  </button>
                </div>
              </div>
              {isOpen && <NotesPreview n={n} />}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function NotesPreview({ n }) {
  const Row = ({ label, value }) => {
    if (!value || !String(value).trim()) return null;
    return (
      <div className="py-2 border-b border-sand/30 last:border-0">
        <div className="text-[10px] uppercase tracking-[0.18em] text-ink-soft mb-0.5">{label}</div>
        <div className="text-sm text-ink whitespace-pre-wrap">{value}</div>
      </div>
    );
  };
  return (
    <div className="mt-3 bg-cream rounded-xl border border-sand/60 p-4">
      <Row label="Presenting issues" value={n.presenting_issues} />
      <Row label="Approach" value={n.approach} />
      <Row label="Observations" value={n.observations} />
      <Row label="Client's strengths" value={n.client_strengths} />
      <Row label="Plan adjustments" value={n.plan_adjustments} />
      <Row label="Decisions" value={n.decisions} />
      <Row label="Follow-up · client" value={n.followup_client} />
      <Row label="Follow-up · counsellor" value={n.followup_counsellor} />
      <Row label="Referrals" value={n.referrals} />
      <Row label="Referral follow-up date" value={n.referral_followup_date} />
      <Row label="Supervision reflection" value={n.supervision_reflection} />
    </div>
  );
}


/**
 * Drawer that opens above the client profile to add/edit session notes for a
 * specific booking. Re-uses the SessionNotesEditor (autosave, attachment, PDF).
 */
function ClientNotesDrawer({ booking, onClose, onCompleted }) {
  return (
    <div
      className="fixed inset-0 bg-ink/40 backdrop-blur-sm z-50 flex items-start justify-center p-4 sm:p-6 overflow-y-auto"
      onClick={onClose}
      data-testid="client-notes-drawer"
    >
      <div
        className="bg-cream-soft border border-sand/70 rounded-3xl p-6 sm:p-8 w-full max-w-3xl mt-6 mb-12 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-ink-soft mb-1">Session notes</div>
            <h3 className="font-serif text-2xl text-ink">{booking.client_name}</h3>
            <p className="text-sm text-ink-soft mt-1">
              {format(new Date(booking.date), "EEE d MMM yyyy")} · {booking.start_time}–{booking.end_time} · {booking.service_name}
            </p>
          </div>
          <button onClick={onClose} className="text-ink-soft hover:text-ink"><X size={20} /></button>
        </div>
        <SessionNotesEditor booking={booking} onCompleted={onCompleted} />
      </div>
    </div>
  );
}

